import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LoaderBar } from 'components'
import { AppSecondaryButton } from 'elements'
import { useNotification } from 'hooks/toast'
import { MasterAnswersService } from 'utility/services/master-answers'
import { MasterAnswersItemsEditor } from './MasterAnswersItemsEditor'
import { NewListModal } from './NewListModal'
import {
    EmptyHint,
    InfoBanner,
    ListsSidebar,
    ListsSidebarBody,
    ListsSidebarHeader,
    ListRow,
    MainCard,
    MainCardBody,
    MainCardHeader,
    MasterListsLayout,
} from './styles'
import {
    getApiErrorMessage,
    normalizeItems,
    normalizeLists,
} from './utils'

export const MasterListsTab = ({ companyId, onNewListClickRef }) => {
    const { successToast, errorToast } = useNotification()
    const [lists, setLists] = useState([])
    const [selectedListId, setSelectedListId] = useState(null)
    const [items, setItems] = useState([])
    const [listsLoading, setListsLoading] = useState(true)
    const [itemsLoading, setItemsLoading] = useState(false)
    const [savingList, setSavingList] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState('create')
    const listItemCountsRef = useRef({})
    const errorToastRef = useRef(errorToast)
    const successToastRef = useRef(successToast)
    errorToastRef.current = errorToast
    successToastRef.current = successToast

    const selectedList = lists.find(l => l.id === selectedListId)

    const loadLists = useCallback(async () => {
        setListsLoading(true)
        try {
            const data = await MasterAnswersService.getListsByCompany(companyId)
            const normalized = normalizeLists(data)
            setLists(normalized)
            setSelectedListId(prev => {
                if (prev && normalized.some(l => l.id === prev)) return prev
                return normalized[0]?.id ?? null
            })
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
            setLists([])
            setSelectedListId(null)
        } finally {
            setListsLoading(false)
        }
    }, [companyId])

    const loadItems = useCallback(async listId => {
        if (!listId) {
            setItems([])
            return
        }
        setItemsLoading(true)
        try {
            const data = await MasterAnswersService.getItems(listId)
            const normalized = normalizeItems(data).sort(
                (a, b) => a.displayOrder - b.displayOrder,
            )
            setItems(normalized)
            listItemCountsRef.current[listId] = normalized.length
            setLists(prev =>
                prev.map(l =>
                    l.id === listId
                        ? { ...l, itemCount: normalized.length }
                        : l,
                ),
            )
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
            setItems([])
        } finally {
            setItemsLoading(false)
        }
    }, [])

    useEffect(() => {
        loadLists()
    }, [loadLists])

    useEffect(() => {
        if (selectedListId) {
            loadItems(selectedListId)
        } else {
            setItems([])
        }
    }, [selectedListId, loadItems])

    const openCreateModal = useCallback(() => {
        setModalMode('create')
        setModalOpen(true)
    }, [])

    useEffect(() => {
        if (onNewListClickRef) {
            onNewListClickRef.current = openCreateModal
        }
    }, [onNewListClickRef, openCreateModal])

    const openRenameModal = () => {
        setModalMode('rename')
        setModalOpen(true)
    }

    const handleSaveList = async ({ listName, listKey }) => {
        if (!listName) {
            errorToast('Display name is required')
            return
        }
        setSavingList(true)
        try {
            if (modalMode === 'create') {
                if (!listKey) {
                    errorToast('Key is required')
                    return
                }
                const created = await MasterAnswersService.createList({
                    companyId,
                    listName,
                    listKey,
                })
                await loadLists()
                const newId = created?.Id ?? created?.id
                if (newId) setSelectedListId(newId)
                successToastRef.current(`Created "${listName}"`)
            } else if (selectedList) {
                await MasterAnswersService.updateList(selectedList.id, {
                    ListName: listName,
                })
                setLists(prev =>
                    prev.map(l =>
                        l.id === selectedList.id ? { ...l, listName } : l,
                    ),
                )
                successToast('List renamed')
            }
            setModalOpen(false)
        } catch (err) {
            errorToast(getApiErrorMessage(err))
        } finally {
            setSavingList(false)
        }
    }

    const toggleListActive = async () => {
        if (!selectedList) return
        const nextActive = !selectedList.isActive
        try {
            await MasterAnswersService.updateList(selectedList.id, {
                IsActive: nextActive,
            })
            setLists(prev =>
                prev.map(l =>
                    l.id === selectedList.id
                        ? { ...l, isActive: nextActive }
                        : l,
                ),
            )
            successToast(
                `List "${selectedList.listName}" is now ${
                    nextActive ? 'active' : 'inactive'
                }`,
            )
        } catch (err) {
            errorToast(getApiErrorMessage(err))
        }
    }

    const getListItemCount = list => {
        if (list.id === selectedListId) return items.length
        if (list.itemCount != null) return list.itemCount
        return '—'
    }

    return (
        <>
            <InfoBanner>
                <span>ℹ️</span>
                <div>
                    Create reusable answer lists per tenant. One list can be
                    referenced from many templates — rename or reorder items once,
                    and every <strong>future audit</strong> that uses the list picks
                    up the change. Already-started audits keep their frozen text
                    (snapshot pattern preserved).
                </div>
            </InfoBanner>

            {listsLoading && <LoaderBar content="Loading master lists..." />}

            {!listsLoading && lists.length === 0 && (
                <EmptyHint style={{ marginBottom: 16 }}>
                    <div className="empty-icon">📋</div>
                    <div
                        style={{
                            fontWeight: 500,
                            color: '#1f2937',
                            marginBottom: 4,
                        }}
                    >
                        No master lists yet
                    </div>
                    <div>
                        Create one with <strong>+ New Master List</strong>, or
                        confirm Master Answer Lists are enabled for company{' '}
                        {companyId} on the server.
                    </div>
                </EmptyHint>
            )}

            {!listsLoading && (
                <MasterListsLayout>
                    <ListsSidebar>
                        <ListsSidebarHeader>
                            <span style={{ flex: 1 }}>Lists for this tenant</span>
                            <AppSecondaryButton
                                type="button"
                                size="mini"
                                onClick={openCreateModal}
                            >
                                + New
                            </AppSecondaryButton>
                        </ListsSidebarHeader>
                        <ListsSidebarBody>
                            {lists.length === 0 && (
                                <EmptyHint style={{ padding: 16 }}>
                                    <div className="empty-icon">📋</div>
                                    No lists yet
                                </EmptyHint>
                            )}
                            {lists.map(list => (
                                <ListRow
                                    key={list.id}
                                    type="button"
                                    $active={list.id === selectedListId}
                                    $inactive={!list.isActive}
                                    onClick={() => setSelectedListId(list.id)}
                                >
                                    <div className="list-icon">
                                        {(list.listName || '?')
                                            .charAt(0)
                                            .toUpperCase()}
                                    </div>
                                    <div className="name">
                                        {list.listName}
                                        <span className="count">
                                            · {getListItemCount(list)}
                                        </span>
                                    </div>
                                    <span className="key">{list.listKey}</span>
                                </ListRow>
                            ))}
                        </ListsSidebarBody>
                    </ListsSidebar>

                    <MainCard>
                        <MainCardHeader>
                            <span>
                                {selectedList
                                    ? `${selectedList.listName} — ${items.length} item${
                                          items.length === 1 ? '' : 's'
                                      }`
                                    : 'Select a list'}
                            </span>
                            {selectedList && (
                                <div className="actions">
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={openRenameModal}
                                    >
                                        Rename
                                    </AppSecondaryButton>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={toggleListActive}
                                    >
                                        {selectedList.isActive
                                            ? 'Deactivate list'
                                            : 'Re-activate list'}
                                    </AppSecondaryButton>
                                </div>
                            )}
                        </MainCardHeader>
                        <MainCardBody>
                            {!selectedList && (
                                <EmptyHint>
                                    <div className="empty-icon">📂</div>
                                    <div
                                        style={{
                                            fontWeight: 500,
                                            color: '#1f2937',
                                            marginBottom: 4,
                                        }}
                                    >
                                        No list selected
                                    </div>
                                    <div>
                                        Pick a list on the left, or create a new
                                        one to get started.
                                    </div>
                                </EmptyHint>
                            )}

                            {selectedList && (
                                <>
                                    <InfoBanner style={{ marginTop: 0 }}>
                                        <span>ℹ️</span>
                                        <div>
                                            <strong>Display type</strong> (Radio /
                                            List Box) and{' '}
                                            <strong>Allow Other Response</strong>{' '}
                                            are <em>question-level</em> settings —
                                            see the Template Editor tab. They live on
                                            each question that references this list,
                                            not on the master list itself.
                                        </div>
                                    </InfoBanner>

                                    {selectedListId && (
                                        <MasterAnswersItemsEditor
                                            key={selectedListId}
                                            listId={selectedListId}
                                            initialItems={items}
                                            itemsLoading={itemsLoading}
                                            onSaved={() =>
                                                loadItems(selectedListId)
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </MainCardBody>
                    </MainCard>
                </MasterListsLayout>
            )}

            <NewListModal
                open={modalOpen}
                mode={modalMode}
                initialName={
                    modalMode === 'rename' ? selectedList?.listName : ''
                }
                initialKey={
                    modalMode === 'rename' ? selectedList?.listKey : ''
                }
                saving={savingList}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveList}
            />

        </>
    )
}
